import { countClass, pointer, queryAllClass, queryClass, runCloud } from './parse';
import type { ParseRecord } from './parse';
import { loadMyCustomer } from './account';
import { loadWatchedLectures } from './studentUtilities';
import { readLocalProgressEntries, type VideoProgressEntry } from './videoProgress';

/** A lecture counts as completed once this fraction was watched. */
const COMPLETION_RATIO = 0.95;

export function isEntryComplete(entry: { time: number; totalTime: number }): boolean {
  return entry.totalTime > 0 && entry.time / entry.totalTime >= COMPLETION_RATIO;
}

export type CertificateRow = ParseRecord & {
  CertificateId?: string;
  LearnerName?: string;
  CourseTitleSnapshot?: string;
  IssuedAt?: string;
  CoursePtr?: { objectId?: string; CourseTitle?: string } | unknown;
};

async function mergedWatchedEntries(): Promise<Map<string, VideoProgressEntry>> {
  let serverEntries: VideoProgressEntry[] = [];
  try {
    serverEntries = (await loadWatchedLectures()).Bookmarks ?? [];
  } catch { /* offline or preview login */ }
  const merged = new Map<string, VideoProgressEntry>();
  for (const entry of [...readLocalProgressEntries(), ...serverEntries]) merged.set(entry.id, entry);
  return merged;
}

function pointerIdOf(value: unknown): string | undefined {
  if (value && typeof value === 'object' && 'objectId' in value) return String((value as { objectId: string }).objectId);
  if (typeof value === 'string') return value.split('$').pop();
  return undefined;
}

export type CourseProgress = {
  courseId: string;
  title: string;
  totalLectures: number;
  completedLectures: number;
  percent: number;
  complete: boolean;
  lastAccessedAt?: string;
};

/**
 * Roll up watch progress per course. Only courses with at least one started
 * lecture appear — there is no enrollment table in the legacy schema.
 */
export async function loadCourseProgressSummaries(): Promise<CourseProgress[]> {
  const watched = await mergedWatchedEntries();
  const watchedIds = [...watched.keys()];
  if (!watchedIds.length) return [];
  const touched = await queryAllClass('elearning_Videos', {
    limit: 100,
    keys: ['CoursePtr'],
    appScoped: false,
    where: (query) => query.containedIn('objectId', watchedIds),
  });
  const courseIds = [...new Set(touched.map((video) => pointerIdOf(video.CoursePtr)).filter((id): id is string => Boolean(id)))];
  const summaries = await Promise.all(courseIds.map((courseId) => loadSingleCourseProgress(courseId, watched)));
  return summaries
    .filter((summary): summary is CourseProgress => summary !== null)
    .sort((left, right) => right.percent - left.percent || right.completedLectures - left.completedLectures);
}

/** Progress for one course; null when the course has no lectures. */
export async function loadSingleCourseProgress(courseId: string, watchedOverride?: Map<string, VideoProgressEntry>): Promise<CourseProgress | null> {
  const watched = watchedOverride ?? await mergedWatchedEntries();
  const courseWhere = (query: { equalTo: (key: string, value: unknown) => void; notEqualTo: (key: string, value: unknown) => void }) => {
    query.equalTo('CoursePtr', pointer('elearning_Course', courseId));
    query.equalTo('Type', 'Original');
    query.notEqualTo('IsDeleted', true);
  };
  const totalLectures = await countClass('elearning_Videos', { where: courseWhere });
  if (!totalLectures) return null;
  const watchedIds = [...watched.keys()];
  const watchedVideos = watchedIds.length ? await queryAllClass('elearning_Videos', {
    limit: 100,
    keys: ['objectId'],
    where: (query) => { courseWhere(query); query.containedIn('objectId', watchedIds); },
  }) : [];
  const completedLectures = watchedVideos.filter((video) => {
    const entry = watched.get(video.objectId);
    return entry && isEntryComplete(entry);
  }).length;
  const [course] = await queryClass('elearning_Course', { limit: 1, keys: ['CourseTitle'], where: (query) => query.equalTo('objectId', courseId) });
  return {
    courseId,
    title: course?.CourseTitle ?? 'Untitled course',
    totalLectures,
    completedLectures,
    percent: Math.round((completedLectures / totalLectures) * 100),
    complete: completedLectures === totalLectures,
    lastAccessedAt: watchedVideos
      .map((video) => watched.get(video.objectId)?.updatedAt)
      .filter((value): value is string => Boolean(value))
      .sort()
      .at(-1),
  };
}

/* ---------- Certificates ---------- */

export type MyCertificate = CertificateRow & { courseTitle?: string; issuedAt?: string };

function resolveCertificate(row: ParseRecord): MyCertificate {
  const course = row.CoursePtr && typeof row.CoursePtr === 'object' ? row.CoursePtr as { CourseTitle?: string } : undefined;
  const rawIssued = row.IssuedAt;
  const iso = typeof rawIssued === 'object' && rawIssued && 'iso' in rawIssued ? String((rawIssued as { iso: string }).iso) : typeof rawIssued === 'string' ? rawIssued : undefined;
  return { ...row, courseTitle: course?.CourseTitle || row.CourseTitleSnapshot || 'AyurGuide course', issuedAt: iso };
}

export async function loadMyCertificates(): Promise<MyCertificate[]> {
  const me = await loadMyCustomer();
  if (!me?.objectId) return [];
  const rows = await queryAllClass('elearning_Certificates', {
    order: '-createdAt',
    include: ['CoursePtr'],
    // The pointer must be requested or the local API drops its include.
    keys: ['CertificateId', 'LearnerName', 'CourseTitleSnapshot', 'IssuedAt', 'CoursePtr'],
    where: (query) => query.equalTo('CustomerPtr', pointer('elearning_Customer', me.objectId)),
  });
  return rows.map(resolveCertificate);
}

export async function loadCertificate(certificateId: string): Promise<MyCertificate | undefined> {
  const rows = await queryClass('elearning_Certificates', {
    limit: 1,
    include: ['CoursePtr'],
    keys: ['CertificateId', 'LearnerName', 'CourseTitleSnapshot', 'IssuedAt', 'CoursePtr'],
    where: (query) => query.equalTo('objectId', certificateId),
  });
  return rows[0] ? resolveCertificate(rows[0]) : undefined;
}

/** Ask the API to verify completion and issue (or return) the certificate. */
export function issueCertificate(courseId: string): Promise<{ objectId: string; CertificateId?: string }> {
  return runCloud('IssueCertificate', { courseId }) as Promise<{ objectId: string; CertificateId?: string }>;
}
